<template>
  <!-- eslint-disable max-len -->
  <div class="root">
    <div v-if="!loading" class="content mobile-adjust">
      <div class="login-card mobile-adjust">
        <div class="login-card-background" />
        <div class="login-form-container" @keydown="listenForEnter">
          <div class="login-title form-element">sign up</div>
          <input class="form-element form-control" :style="$v.user.firstName.$error ? validationFailedStyle : ''" placeholder="First Name" v-model.trim="user.firstName" :validator="$v.user.firstName" v-focus autofocus @input="allowValidationErrors=true" @blur="()=>{ if (allowValidationErrors) $v.user.firstName.$touch() }" name="firstName"/>
          <input class="form-element form-control" :style="$v.user.lastName.$error ? validationFailedStyle : ''" placeholder="Last Name" v-model.trim="user.lastName" :validator="$v.user.lastName" @input="allowValidationErrors=true" @blur="()=>{ if (allowValidationErrors) $v.user.lastName.$touch() }" name="lastName"/>
          <input class="form-element form-control" :style="$v.user.email.$error ? validationFailedStyle : ''" placeholder="Email" v-model.trim="user.email" :validator="$v.user.email" :custom-errors="[{validator: 'email', message: 'Must be a valid email address'}]" @input="allowValidationErrors=true" @blur="()=>{ if (allowValidationErrors) $v.user.email.$touch() }" type="email" name="email"/>
          <input class="form-element form-control" :style="$v.user.password.$error ? validationFailedStyle : ''" placeholder="Enter Password" v-model.trim="user.password" :validator="$v.user.password" @input="allowValidationErrors=true" @blur="()=>{ if (allowValidationErrors) $v.user.password.$touch() }" type="password"/>
          <input class="form-element form-control" :style="$v.user.confirmPassword.$error ? validationFailedStyle : ''" placeholder="Confirm Password" v-model.trim="user.confirmPassword" :validator="$v.user.confirmPassword" @input="allowValidationErrors=true" @blur="()=>{ if (allowValidationErrors) $v.user.confirmPassword.$touch() }" type="password" required confirm-password :custom-errors="[{ validator: 'sameAsPassword', message: 'Passwords must match' }]"/>

          <div v-if="shouldDisplayStudentIdField">
            <input class="form-element form-control" :style="$v.user.studentId.$error ? validationFailedStyle : ''" placeholder="Enter Student/Employee ID" v-model.trim="user.studentId" :validator="$v.user.studentId" @input="allowValidationErrors=true" @blur="()=>{ if (allowValidationErrors) $v.user.studentId.$touch() }" />
            <input class="form-element form-control" :style="$v.user.confirmStudentId.$error ? validationFailedStyle : ''" placeholder="Confirm Student/Employee ID" v-model.trim="user.confirmStudentId" :validator="$v.user.confirmStudentId" @input="allowValidationErrors=true" @blur="()=>{ if (allowValidationErrors) $v.user.confirmStudentId.$touch() }" required :custom-errors="[{ validator: 'sameAsStudentId', message: 'Student/Employee IDs must match' }]"/>
          </div>

          <div class="login-form-footer">
            <div class="form-element">
              <p v-if="shouldDisplayLogin">
                Already have an account? <router-link :to="signinUrl" style="color: rgb(80, 80, 80);">Sign in here.</router-link>
              </p>
            </div>
            <div class="login-form-login-button-container form-element">
              <button
                type="submit"
                class="btn login-btn lgn-lg-btn"
                @click.prevent="onSubmit"
                :disabled="submitting || $v.user.$invalid">
                Next
              </button>
            </div>
          </div>
          <div class="error-container">
            <template v-if="$v.user.firstName.$error">
              <div class="error-msg" v-if="!$v.user.firstName.required">First name is required</div>
              <div class="error-msg" v-else-if="!$v.user.firstName.maxLength">First name must be {{ $v.user.firstName.$params.maxLength.max }} characters or less</div>
            </template>

            <template v-else-if="$v.user.lastName.$error">
              <div class="error-msg" v-if="!$v.user.lastName.required">Last name is required</div>
              <div class="error-msg" v-else-if="!$v.user.lastName.maxLength">Last name must be {{ $v.user.lastName.$params.maxLength.max }} characters or less</div>
            </template>

            <template v-else-if="$v.user.email.$error">
              <div class="error-msg" v-if="!$v.user.email.required">Email is required</div>
              <div class="error-msg" v-else-if="!$v.user.email.email">Email must be a  valid email</div>
              <div class="error-msg" v-else-if="!$v.user.email.maxLength">Email must be {{ $v.user.email.$params.maxLength.max }} characters or less</div>
            </template>

            <template v-else-if="$v.user.password.$error">
              <div class="error-msg" v-if="!$v.user.password.required">Password is required</div>
              <div class="error-msg" v-else-if="!$v.user.password.minLength">Password must be {{ $v.user.password.$params.minLength.min }} characters or more</div>
              <div class="error-msg" v-else-if="!$v.user.password.maxLength">Password must be {{ $v.user.password.$params.maxLength.max }} characters or less</div>
            </template>

            <template v-else-if="$v.user.confirmPassword.$error">
              <div class="error-msg" v-if="!$v.user.confirmPassword.required">You must confirm your password</div>
              <div class="error-msg" v-else-if="!$v.user.confirmPassword.sameAsPassword">Password must match</div>
            </template>

            <template v-else-if="$v.user.studentId.$error">
              <div class="error-msg" v-if="!$v.user.studentId.required">Student/Employee ID is required</div>
            </template>

            <template v-else-if="$v.user.confirmStudentId.$error">
              <div class="error-msg" v-if="!$v.user.confirmStudentId.required">You must confirm your student/employee ID</div>
              <div class="error-msg" v-else-if="!$v.user.confirmStudentId.sameAsStudentId">Student/Employee IDs must match</div>
            </template>
          </div>
        </div>
      </div>
      <div class="login-card">
        <div class="login-card-background" :style="brandBackgroundStyle" />
        <div class="login-brand-container">
          <div class="brand-top">
            <div class="brand-image" :style="`background-image: url('${brandImage}');`" />
          </div>
          <div style="border: 1px solid white;"/>
          <div class="brand-bottom" v-html="brandText"/>
        </div>
      </div>
    </div>
    <div v-else>
      LOADING
      <!-- TODO SPINNER -->
    </div>
  </div>
</template>

<script>
import { required, requiredIf, minLength, maxLength, sameAs, email } from 'vuelidate/lib/validators';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import TextInput from '../ui/inputs/TextInput';

export default {
  components: {
    TextInput
  },
  props: {
    registrationLink: String,
    classId: String,
    code: String,
    role: String
  },
  data() {
    return {
      loading: false,
      link: null,
      additionalFieldsToDisplay: [],
      unwatch: null,
      allowValidationErrors: false,
      user: {
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',

        // additional fields
        studentId: '',
        confirmStudentId: ''
      },
      emailExists: false,
      submitting: false
    };
  },
  async created() {
    if (this.role) {
      this.setSignInRole(this.role);
    }
    this.user.email = this.email;
  },
  async mounted() {
    if (this.me && this.me.email) {
      await this.alreadySignedIn();
    } else {
      if (this.registrationLink) {
        try {
          this.loading = true;
          this.link = await this.getRegistrationLinkByLink(this.registrationLink);
          console.log({ link: this.link });
          if (this.link.signInConfig) {
            this.setActiveSignInConfig(this.link.signInConfig);
          } else {
            this.getActiveSignInConfig();
          }
          this.getActiveEnvironment({ path: this.$route.path });
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }

      try {
        this.loading = true;
        this.loadAdditionalFields();
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    }
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeSignInConfig',
      'classTypeNameLower',
      'me'
    ]),
    shouldDisplayLogin() {
      return this.classId || this.code || this.registrationLink;
    },
    shouldDisplayStudentIdField() {
      return this.additionalFieldsToDisplay.includes('studentId');
    },
    skipEmailVerification() {
      return this.additionalFieldsToDisplay.includes('skipEmailVerification');
    },
    signinUrl() {
      return this.$route.fullPath.replace('signup', 'signin');
    },
    validationFailedStyle() {
      return 'box-shadow: 0 0 2px red; background-color: rgba(255, 0, 0, 0.1);';
    },
    brandBackgroundStyle() {
      return {
        'background-color': this.activeSignInConfig.brandPrimaryColor
      };
    },
    brandImage() {
      return this.resolveAssetPath(this.activeSignInConfig.midsectionImage, 'ui/customer');
    },
    brandText() {
      return this.activeSignInConfig.midsectionText || 'RedmerU is a hosting platform for digital case studies also known as “gamulations” for academic use in higher education institutions. These applications provide an experiential learning experience for college students both in the classroom and from home in a distance learning environment.';
    }
  },
  methods: {
    ...mapMutations([
      'setSignInRole',
      'setActiveEnvironment',
      'setActiveSignInConfig'
    ]),

    ...mapActions([
      'getActiveSignInConfig',
      'signup',
      'flashDanger',
      'flashPrimary',
      'getActiveEnvironment',
      'getClassAdditionalUserFields',
      'getRegistrationLinkAdditionalUserFields',
      'getRegistrationLinkByLink',
      'getSignInConfig'
    ]),

    async loadAdditionalFields() {
      if (this.classId) {
        this.additionalFieldsToDisplay = await this.getClassAdditionalUserFields(this);
      }
      if (this.registrationLink && this.link) {
        this.additionalFieldsToDisplay = await this.getRegistrationLinkAdditionalUserFields(this.link);
      }
    },

    listenForEnter(evt) {
      if (this.$v.$invalid) return;

      /** @type {KeyboardEvent} */
      const ev = evt;
      if (ev.key === 'Enter') {
        this.onSubmit();
      }
    },

    async onSubmit() {
      this.submitting = true;
      try {
        await this.signup({
          ...this.user,
          registrationLink: this.registrationLink,
          classId: this.classId,
          code: this.code,
          role: this.role
        });

        // Note: This is a gross hack but it literally does not matter
        // This is all consumed in signin
        window.preventSignupEmailConsumption = true;
        localStorage.setItem('signup-used-email', this.user.email);

        const successMessage =
          this.skipEmailVerification
            ? 'Successfully created new account. You can now log in.'
            : `Successfully created new account. An email was sent to ${
              this.user.email} with a verification link. Click that link to verify your email and then you can log in.`;

        this.flashPrimary({
          message: successMessage,
          modal: true,
          onCloseModal: () => { this.$router.replace('/signin'); this.$router.go(); }
        });
        this.submitting = false;
      } catch (err) {
        this.submitting = false;
        if (err && err.response && err.response.data) {
          const message = err.response.data;
          if (typeof (message) === 'string') {
            if (message.includes('already exists')) {
              this.emailExists = true;
            }
            this.flashDanger({ message, modal: true });
            return;
          }
        }
        this.submitting = false;
        const message = 'We couldn\'t sign you up, please try again';
        this.flashDanger({ message, modal: true });
        // this.$router.replace('/signin');
      }
    },

    async alreadySignedIn() {
      this.submitting = true;
      try {
        await this.signup({
          ...this.me,
          classId: this.classId,
          code: this.code,
          registrationLink: this.registrationLink
        });
        this.submitting = false;
        this.flashPrimary({
          message: `Successfully added ${this.registrationLink ? 'content' : (this.classId ? this.classTypeNameLower : 'gamulation')} to this account (${
            this.me.email}).`,
          modal: true,
        });
        this.$router.replace('/');
        // this.$router.go();
      } catch (err) {
        this.submitting = false;
        const message = `Failed to add the ${this.registrationLink ? 'content' : (this.classId ? this.classTypeNameLower : 'gamulation')} to your account, please try again`;
        this.flashDanger({ message, modal: true });
        this.$router.replace('/');
        // this.$router.go();
      }
    }
  },
  validations: {
    user: {
      firstName: {
        required,
        maxLength: maxLength(35)
      },
      lastName: {
        required,
        maxLength: maxLength(35)
      },
      password: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(128)
      },
      email: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(254),
        email
      },
      confirmPassword: {
        required,
        sameAsPassword: sameAs('password')
      },
      studentId: {
        required: requiredIf(function() { return this.shouldDisplayStudentIdField; })
      },
      confirmStudentId: {
        required: requiredIf(function() { return this.shouldDisplayStudentIdField; }),
        sameAsStudentId: sameAs('studentId')
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.signin-form {
  width: 75%;
  position: absolute;
  left: 30px;
  top: 110px;

  .title {
    width: 100%;
    margin-bottom: 6px;
    font-size: 95%;
  }

  .error-msg {
    color: var(--signInTextColor);
    font-size: 85%;
    margin-top: 7px;
    margin-bottom: 7px;
  }

  input {
    background-color: #99999933;
    opacity: .9;
    border: 1px solid #dddddd;
    border-radius: 10px;
    margin-bottom: 12px;
    color: #dddddd;
  }

  input::placeholder {
    color: #bbbbbb;
    text-transform: lowercase;
  }

  // input[type="email"] {
  //   border-top-left-radius: 10px;
  //   border-top-right-radius: 10px;
  //   border-bottom: 1px solid white;
  // }

  // input[type="password"] {
  //   border-bottom-left-radius: 10px;
  //   border-bottom-right-radius: 10px;
  //   border-top: 1px solid white;
  // }

  .login-btn {
    width: 42%;
  }

  .pw-reset-btn {
    padding: 5px;
  }

  .forgot-pw-btn {
    padding: 5px;
    filter: saturate(0.2);
  }
}

.loader {
  border: 4px solid #f3f3f3aa;
  border-top: 4px solid #44484baa;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  margin: auto;
  animation: spin .75s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.root {
  // 40px == the navbar height.  If that changes, change this.
  height: calc(100vh - 40px);
  background-size: cover;
  width: 100%;
  position: relative;
  // font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-weight: 100;
}

.content {
  input {
    color: rgb(34, 34, 34);
  }
  // color: var(--signInTextColor);
  color: rgb(34,34,34);
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;

  height: 45%;
  width: 100%;
  // padding-left: 22%;
  // padding-right: 22%;
  position: absolute;
  top: 15%;
}

@media only screen and (max-width: 757px)  {
  .content.mobile-adjust {
    // height: fit-content !important;
    top: 0 !important;
  }
}

.login-card {
  position: relative;
  min-width: calc(min(350px, 98vw));
  min-height: 450px;
  margin: 10px;
  width: 25%;
  height: 100%;
  border-radius: 15px;
  border: 2px solid white;
  overflow: hidden;
}

@media only screen and (max-width: 757px)  {
  .login-card.mobile-adjust {
    min-height: 0 !important;
    height: fit-content !important;
  }
}

.login-card-background {
  position: absolute;
  top: 0px;
  right: 0px;
  left: 0px;
  bottom: 0px;

  background-color: white;
  mask-image: url('/static/ui/customer/EA1_loginBox-White.png');
  mask-size: 110% 110%;
  mask-position: center;
}

.login-form-container
{
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px;
  overflow-y: auto;
}

.login-form-footer
{
  display: flex;
  justify-content: space-between;
}

.login-form-login-button-container
{
  display: flex;
  justify-content: flex-end;
}

.login-brand-container
{
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-evenly;
  padding: 20px;
  overflow-y: auto;
  color: white;
}

.brand-top
{
  height: 48%;
}

.brand-bottom
{
  height: 48%;
  font-size: 18px;
}

.brand-image
{
  position: relative;
  width: 100%;
  height: 100%;
  padding: 30px;
  background-origin: content-box;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.lgn-lg-btn
{
  font-size: 18px;
  min-width: 100px;
  height: 40px;
  padding: 5px;
}

.login-title
{
  font-size: 30px;
  text-transform: uppercase;
  font-weight: 50;
}

.form-element
{
  margin: 5px 0;
}

.error-container
{
  margin-top: 5px;
  height: 20px;
}

.error-msg
{
  color: darkred;
  font-weight: 500;
}

.brand-bottom
{
  color: var(--signInTextColor);
}

.eula-prompt-container
{
  position: fixed;
  top: calc(20vh - 15px);
}
</style>



// WEBPACK FOOTER //
// src/components/auth/Signup.vue